import { MenuConfig } from "@/config/types";
import {
  Bolt,
  Users,
  Building2,
  Ship,
  UserCheck,
  Shield,
  Sparkles,
  Kanban,
  FileText,
  GitBranch,
  LayoutDashboard,
  Settings,
  RefreshCw,
  Clock,
  DollarSign,
  MapPin,
  Factory,
  ClipboardList,
  Cog,
  Truck,
} from "lucide-react";

// =============================================================================
// Types
// =============================================================================

export interface ModuleMenuConfig {
  dashboard: MenuConfig;
  sections: Array<{ id: string; menu: MenuConfig }>;
  placeholder?: string;
}

// =============================================================================
// PLATFORM MENU (SaaS Admin)
// =============================================================================

export const MENU_SIDEBAR_MAIN: MenuConfig = [
  {
    children: [
      { title: 'Dashboard', path: '/dashboard', icon: Bolt },
      { title: 'Tenants', path: '/platform/tenants', icon: Building2 },
      { title: 'Users', path: '/users', icon: Users },
      { title: 'AI Settings', path: '/platform/ai-settings', icon: Sparkles },
      { title: 'Platform Settings', path: '/platform/settings', icon: Settings },
    ],
  }
];

// =============================================================================
// MODULE_CONFIG - Single source of truth for all tenant module menus
// =============================================================================

export const MODULE_CONFIG: Record<string, ModuleMenuConfig> = {
  // Dashboard Module
  dashboard: {
    dashboard: [{ children: [{ title: 'Dashboard', path: '/dashboard', icon: LayoutDashboard }] }],
    sections: [
      { 
        id: 'people', 
        menu: [{
          title: 'People',
          children: [
            { title: 'Employees', path: '/employees', icon: UserCheck },
            { title: 'Roles & Permissions', path: '/settings/roles', icon: Shield },
          ]
        }]
      },
      { 
        id: 'administration', 
        menu: [{
          title: 'Administration',
          children: [
            { title: 'Dealership Settings', path: '/settings/dealership', icon: Building2 },
            { title: 'Templates', path: '/settings/templates', icon: FileText },
          ]
        }]
      },
    ],
  },

  // CRM Module
  crm: {
    dashboard: [{ children: [{ title: 'CRM Dashboard', path: '/crm', icon: LayoutDashboard }] }],
    sections: [
      { 
        id: 'leads', 
        menu: [{
          title: 'Leads',
          children: [
            { title: 'All Leads', path: '/crm/leads', icon: Users },
            { title: 'Kanban Board', path: '/crm/leads/kanban', icon: Kanban },
          ]
        }]
      },
      { 
        id: 'sales', 
        menu: [{
          title: 'Sales',
          children: [
            { title: 'Quotes', path: '/crm/quotes', icon: FileText },
            { title: 'Pipelines', path: '/crm/pipelines', icon: GitBranch },
          ]
        }]
      },
      { 
        id: 'contacts', 
        menu: [{
          title: 'Contacts',
          children: [
            { title: 'Customers', path: '/crm/customers', icon: Users },
          ]
        }]
      },
    ],
  },

  // Inventory Module
  inventory: {
    dashboard: [{ children: [{ title: 'Inventory Dashboard', path: '/inventory', icon: LayoutDashboard }] }],
    sections: [
      {
        id: 'inventory',
        menu: [{
          title: 'Inventory',
          children: [
            { title: 'All Boats', path: '/inventory/boats', icon: Ship },
            { title: 'All Engines', path: '/inventory/engines', icon: Cog },
            { title: 'All Trailers', path: '/inventory/trailers', icon: Truck },
          ]
        }]
      },
      {
        id: 'inventory-reports',
        menu: [{
          title: 'Reports',
          children: [
            { title: 'Summary', path: '/inventory/reports/summary', icon: ClipboardList },
            { title: 'Days on Market', path: '/inventory/reports/days-on-market', icon: Clock },
            { title: 'Pricing Analysis', path: '/inventory/reports/pricing', icon: DollarSign },
            { title: 'By Location', path: '/inventory/reports/by-location', icon: MapPin },
            { title: 'By Manufacturer', path: '/inventory/reports/by-manufacturer', icon: Factory },
          ]
        }]
      },
      {
        id: 'inventory-administration',
        menu: [{
          title: 'Administration',
          children: [
            { title: 'Inventory Settings', path: '/inventory/settings', icon: Settings },
            { title: 'WordPress Sync', path: '/inventory/wp-sync', icon: RefreshCw },
          ]
        }]
      },
    ],
  },

  // Service Module (placeholder)
  service: {
    dashboard: [{ children: [{ title: 'Service Dashboard', path: '/service', icon: LayoutDashboard }] }],
    sections: [],
    placeholder: 'Service module coming soon',
  },

  // Finance Module (placeholder)
  finance: {
    dashboard: [{ children: [{ title: 'Finance Dashboard', path: '/finance', icon: LayoutDashboard }] }],
    sections: [],
    placeholder: 'Finance module coming soon',
  },
};

// Legacy exports for backwards compatibility
export const MENU_SIDEBAR_TENANT: MenuConfig = MODULE_CONFIG.dashboard.dashboard;
export const MENU_TENANT_MAIN: MenuConfig = MODULE_CONFIG.dashboard.dashboard;
